<template>
  <v-container class="ma-auto categories__container <xs:(wd-relative wd-z-regionListContainer)" fluid>
    <client-only>
      <div v-if="isMobile" v-intersect="{ handler: onIntersect, options: { threshold: 0 } }" class="target-box"></div>
    </client-only>
    <category-info-sheet-mobile
      v-show="isMobile"
      :region="region"
      :has-geolocation="hasGeolocation"
      :filters="filterOptions"
      :search-key-word="searchKeyWord"
      :total-count="totalCount"
      :is-fixed-top="isFixedTop"
      :from-page="FilterFromPage.SEARCH"
      :has-category-info="false"
      :is-branch-card-mode="isBranchCardMode"
      @update-condition="updateFilter"
    />
    <category-info-sheet
      v-show="!isMobile"
      :region="region"
      :has-geolocation="hasGeolocation"
      :filters="filterOptions"
      :search-key-word="searchKeyWord"
      :total-count="totalCount"
      :from-page="FilterFromPage.SEARCH"
      :has-category-info="false"
      :is-branch-card-mode="isBranchCardMode"
      @update-condition="updateFilter"
    />

    <div class="categories__wrapper">
      <!-- 商品列表 -->
      <v-sheet v-if="isBranchCardMode ? branchesInfo.branches.length > 0 : searchProductInfo.products.length > 0" class="categories-list" rounded>
        <div class="categories-list__wrapper">
          <v-row v-if="loadingList" class="product-list">
            <v-col v-for="number in 20" :key="number" class="product-list__col" cols="12" md="3" sm="6">
              <div class="product__box">
                <v-skeleton-loader class="mx-auto" max-width="300" type="image, list-item-three-line" />
              </div>
            </v-col>
          </v-row>

          <v-row v-else class="product-list">
            <template v-if="isBranchCardMode">
              <v-col v-for="branch in branchesInfo.branches" :key="branch.bid" class="product-list__col" cols="12" md="3" sm="6">
                <div class="product__box">
                  <BranchCard :branch="branch" :bring-query="false" />
                </div>
              </v-col>
            </template>

            <template v-else>
              <v-col v-for="product in searchProductInfo.products" :key="product.product_id" class="product-list__col" cols="12" md="3" sm="6">
                <div class="product__box">
                  <product-card :product="product" :bring-query="false" />
                </div>
              </v-col>
            </template>
          </v-row>

          <loading v-if="loadingList" class="list-loading" :width="72" :height="72" color="orange" />
        </div>

        <pagination class="mb-9" :page="pagingState.page" :visible="8" :total-count="totalCount" :size="pagingState.size" @update="changePage" />
      </v-sheet>
      <div v-else class="categories-list--empty">
        <p class="empty-text">{{ $t('filter_empty_message') }}</p>
      </div>
    </div>
  </v-container>
</template>

<script setup>
import { useABTestingStore } from '@/store-pinia/abtesting'
import { useBreakpoints } from '@/composition/useBreakpoints'
import { provideGtmSource } from '@/composition/useGtmSource'
import { useRegionSearchNavigation } from '@/composables/useRegionSearchNavigation'
import { getCategoryDefaultFiltersFromQuery } from '@/utils/filter'
import { useRouteQueryFilterSync } from '@/composables/useRouteQueryFilterSync'
import { GtmSource } from '@/view-models/gtm'
import { FilterFromPage } from '@/constants/filters'

import { ASYNC_DATA_KEYS, getAsyncDataKey } from '@/constants/async-data-keys'

import CategoryInfoSheet from '@/components/page/categories/category-info-sheet.vue'
import CategoryInfoSheetMobile from '@/components/page/categories/mobile/category-info-sheet--mobile.vue'

const ProductCard = defineAsyncComponent(() => import('@/components/card/product.vue'))
const BranchCard = defineAsyncComponent(() => import('@/components/card/BranchCard.vue'))
const Pagination = defineAsyncComponent(() => import('@/components/pagination/pagination'))
const Loading = defineAsyncComponent(() => import('@/components/icons/loading'))

const nuxtApp = useNuxtApp()
const { $store: store, $pinia } = nuxtApp
const route = useRoute()
const abTestingStore = useABTestingStore($pinia)
const { cardMode } = storeToRefs(abTestingStore)
const { isMobile } = useBreakpoints()

const { sendGTMViewItemList } = useGTMViewItemList()

const isFixedTop = ref(false)
const loadingList = ref(false)

const paginationParamOfList = computed(() => ({
  start: (pagingState.page - 1) * pagingState.size,
  size: pagingState.size,
}))
const regionID = computed(() => Number(route.params.rid || 0))

const region = computed(() => store.getters['region/region'])
const searchKeyWord = computed(() => store.getters['search/searchKeyword'])
const searchProductInfo = computed(() => store.getters['product/categoryProductInfo'])
const branchesInfo = computed(() => store.getters['branch/branchesInfo'])
const sortType = computed(() => store.getters['filter/sortType'])
const selectedFilters = computed(() => store.getters['filter/filters'])

const isBranchCardMode = computed(() => cardMode.value === 'branch')
const totalCount = computed(() => (isBranchCardMode.value ? branchesInfo.value.totalCount : searchProductInfo.value.totalCount))

useHead(() => {
  const pageTitle = `${region.value?.name || ''} | ${nuxtApp.$i18n.t('funnow_title')}`
  return {
    title: pageTitle,
    meta: [
      { hid: 'title', name: 'title', content: pageTitle },
      { hid: 'og:title', property: 'og:title', content: pageTitle },
      { name: 'robots', content: 'noindex' },
    ],
  }
})

onUnmounted(() => {
  store.dispatch('search/setSearchKeyword', '')
  store.dispatch('filter/resetFilterState')
})
provideGtmSource(GtmSource.SEARCH_RESULT_LIST)

const { pagingState, userPosition, hasGeolocation } = useRegionSearchNavigation({
  fetchList: getProductList,
})

const { data } = await useAsyncData(getAsyncDataKey(ASYNC_DATA_KEYS.SEARCH_PAGE_DATA, [regionID.value]), async () => {
  try {
    const { query } = route

    const [_regionRes, filtersOptionsRes] = await Promise.all([
      store.dispatch('region/getRegion', regionID.value),
      fetchFilterOptions({ regionID: regionID.value }),
    ])

    // NOTE：不確定搜尋頁面內的 filter component 定義，先使用 categoryDefaultFiltersFromQuery 來處理。
    const filtersFromRouterQuery = getCategoryDefaultFiltersFromQuery(query, filtersOptionsRes, hasGeolocation.value)

    if (filtersFromRouterQuery.filter_keyword) {
      store.dispatch('search/setSearchKeyword', filtersFromRouterQuery.filter_keyword)
    }

    updateSelectedFilters(filtersFromRouterQuery)
    store.dispatch('category/resetCategory')

    // NOTE:不確定會不會 API 是否支援 filter_tags
    const filterData = {
      sortby: sortType.value,
      filter_regionid: regionID.value,
      ...paginationParamOfList.value,
      ...filtersFromRouterQuery,
    }
    await fetchSearchItemsByCardMode(filterData)

    return {
      filterOptions: filtersOptionsRes,
    }
  } catch (err) {
    console.error(err)
    throw showError({ statusCode: 404, message: 'Post not found', fatal: true })
  }
})

const filterOptions = computed(() => (data.value ? data.value.filterOptions : {}))

// Sync all filters when route query changes (back/forward navigation)
useRouteQueryFilterSync({
  filterOptions,
  hasGeolocation,
  selectedFilters,
  pagingState,
  updateSelectedFilters,
  getProductList,
  onBeforeSync(newQuery) {
    if (newQuery.filter_keyword) store.dispatch('search/setSearchKeyword', newQuery.filter_keyword)
    else store.dispatch('search/setSearchKeyword', '')
  },
})

// lifecycle
onMounted(() => {
  sendGTMViewItemList()
})
// functions

function onIntersect(_, entries) {
  const isFixed = !entries[0].isIntersecting
  isFixedTop.value = isFixed
}

function updateFilter({ sort, filter }) {
  const currentPage = pagingState.page
  const firstPage = 1
  const newQuery = { ...filter, page: firstPage }

  updateSelectedFilters(newQuery)
  updatePagingPage(firstPage)
  navigateTo({ query: newQuery })

  if (currentPage === firstPage) {
    getProductList()
  }
}

function changePage(newPage) {
  pagingState.page = newPage
}

function updatePagingPage(newPage) {
  pagingState.page = newPage
}

function updateSelectedFilters(newPartialFilters) {
  store.dispatch('filter/setFilters', newPartialFilters)
}

function getListPayloadWithoutFilterOptions() {
  const data = {
    sortby: sortType.value,
    filter_regionid: regionID.value,
    ...paginationParamOfList.value,
    ...(hasGeolocation.value && { lng: userPosition.lng, lat: userPosition.lat }),
    ...(searchKeyWord.value && { filter_keyword: searchKeyWord.value }),
  }
  return data
}

async function getProductList() {
  const payloadWithoutFilterOptions = getListPayloadWithoutFilterOptions()

  // NOTE: page 不是 API 的參數，所以需要刪除
  const { page, ...payload } = {
    ...payloadWithoutFilterOptions,
    ...selectedFilters.value,
  }

  try {
    loadingList.value = true
    await fetchSearchItemsByCardMode(payload)
  } catch (error) {
    console.error('fetchSearchItemsByCardMode: error', error)
  } finally {
    loadingList.value = false
  }
}

async function fetchSearchItemsByCardMode(payload) {
  const dispatchAction = isBranchCardMode.value ? 'branch/getBranchList' : 'product/getProductList'
  await store.dispatch(dispatchAction, payload)
  sendGTMViewItemList()
}

async function fetchFilterOptions(params) {
  try {
    const filtersRes = await store.dispatch('filter/getFilters', params)

    return filtersRes.code === 0 ? filtersRes.data : {}
  } catch (error) {
    console.error('fetchFilterOptions: error', error)

    return {}
  }
}
</script>

<style scoped lang="scss" src="@/assets/sass/pages/categories.scss"></style>
